import {
  CheckCircle,
  HourglassTop,
  ImageAspectRatio,
  Link as MuiLink,
  Pending,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  DetailAccordion,
  DetailAccordionContainer,
  DetailAccordionDetails,
  DetailAccordionSummary,
} from '~/components/DetailAccordion';
import { DetailSection } from '~/components/DetailSection';
import { Link } from '~/components/link';
import { formatBytes } from '~/lib/units';
import type { MediaItemRenditionsProps__MediaItem } from './MediaItemRenditions.generated';

/* GraphQL */ `#graphql
fragment MediaItemRenditionsProps__MediaItem on MediaItem {
  id
  kind
  originalMetadata
  renditions {
    filename
    height
    id
    variant
    size
    status
    uri
    width
  }
}
`;

export interface MediaItemRenditionsProps {
  mediaItem: MediaItemRenditionsProps__MediaItem;
}

const RENDITION_VARIANT_LABELS = new Map<string, string>([
  ['WEBP', 'WebP'],
  ['MP4', 'MP4'],
  ['WEBM', 'WebM'],
]);

export const renditionVariantLabel = (variant?: string | null) =>
  variant ? RENDITION_VARIANT_LABELS.get(variant) : undefined;

export const MediaItemRenditions = ({ mediaItem }: MediaItemRenditionsProps) => {
  const { t } = useTranslation();

  return (
    <DetailAccordionContainer>
      {mediaItem.renditions
        .filter((rendition) => rendition.variant != null)
        .map((item, index) => (
          <DetailAccordion defaultExpanded key={index}>
            <DetailAccordionSummary>
              {renditionVariantLabel(item.variant) ?? item.status}
            </DetailAccordionSummary>
            <DetailAccordionDetails>
              <DetailSection
                icon={<MuiLink />}
                title={t('components.media_item_info.info.file')}
                value={
                  item.uri && item.filename ? (
                    <Link to={item.uri} target="_blank">
                      {item.filename}
                    </Link>
                  ) : (
                    '—'
                  )
                }
              />
              <DetailSection
                icon="Kb"
                title={t('common.labels.size')}
                value={item.size != null ? formatBytes(item.size) : '—'}
              />
              <DetailSection
                icon={<ImageAspectRatio />}
                title={t('components.media_item_info.specs.dimensions')}
                value={
                  item.width != null && item.height != null ? `${item.width} x ${item.height}` : '—'
                }
              />
              <DetailSection
                icon={
                  item.status.toLowerCase() === 'ready' ? (
                    <CheckCircle />
                  ) : item.status.toLowerCase() === 'pending' ? (
                    <Pending />
                  ) : (
                    <HourglassTop />
                  )
                }
                title={t('common.labels.status')}
                value={item.status.toLowerCase()}
              />
            </DetailAccordionDetails>
          </DetailAccordion>
        ))}
    </DetailAccordionContainer>
  );
};
